import { Injectable, signal } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpService } from '@shared/services/http.service';
import { environment as env } from 'environments/environment';
import { BACKOFFICE_API_URL, DISCREPANCY_ROUTE, DISCREPANCY_CREATE_ROUTE, EMPLOYEE_LIST_ROUTE, GETFORM_ROUTE_URL, PAYSLIPS_ROUTE, WEBSITE_API_URL, TIME_TRACKING_ROUTE, EMPLOYER_ROUTE, SHIFTS_MANAGEMENT_ACTIVE_ROUTE, SHIFTS_MANAGEMENT_SETTINGS_ROUTE, SHIFTS_MANAGEMENT_TEMPLATES_ROUTE } from '@shared/constants/constants';
import { PaginationType } from '@shared/models/pagination';
import { SortType } from '@shared/models/sorting';
import { ROUTES } from '@shared/constants/routes';

@Injectable({
  providedIn: 'root'
})
export class EmployerService {

  employeeId = signal<string | null>(null);
  employee = signal<any | null>(null);
  constructor(private readonly httpService: HttpService) {}

  private buildParams(
    filters: Record<string, any> | null,
    pagination?: PaginationType | null,
    sorting?: SortType | null
  ): HttpParams {
    let params = new HttpParams();
    if (pagination) {
      params = params.set('page', String(pagination.page)).set('perPage', String(pagination.perPage));
    }
    if (filters) {
      Object.keys(filters).forEach(k => { params = params.set(k, String(filters[k])); });
    }
    if (sorting) {
      params = params.set('_sort', sorting.column);
      if (sorting.direction != null) params = params.set('_order', sorting.direction);
    }
    return params;
  }

  setEmployeeContext(employeeId: string | null, employeeData: any | null) {
    this.employeeId.set(employeeId);
    this.employee.set(employeeData);
  }

  getEmployeesList(
    employerId: string | number | null,
    pagination: PaginationType,
    filters: any | null = null,
    sorting: SortType | null = null,
    skipLoader: boolean = false
  ): Observable<any> {
    const url = `${env.baseUrl}${BACKOFFICE_API_URL}${ROUTES.EMPLOYER}/${employerId}/${EMPLOYEE_LIST_ROUTE}`;
    const params = this.buildParams(filters, pagination, sorting);
    return this.httpService.get<any>(url, { skipLoader, params });
  }

  // TODO: Completare quando mockoon è tolto usando le api reali 
  getEmployeeDetail(
    employerId: string | number | null, // id dell'azienda
    employeeId: string | number | null, // id del dipendente
    skipLoader: boolean = false
  ): Observable<any> {
    let url = `${env.baseUrl}${BACKOFFICE_API_URL}${EMPLOYEE_LIST_ROUTE}`;
    return this.httpService.get<any>(url, { skipLoader });
  }

  storeDiscrepancies(
    idPayslip: string | number,
    data: any,
    skipLoader: boolean = false
  ) {
    // TODO: capire col backend che rotta serve
    const url = `${env.baseUrl}${WEBSITE_API_URL}${ROUTES.EMPLOYER}/${PAYSLIPS_ROUTE}/${idPayslip}/${DISCREPANCY_ROUTE}`;
    return this.httpService.post<any>(url, data, { skipLoader });
  }

  getFormDiscrepancies(
    skipLoader: boolean = false
  ) {
    let url = env.baseUrl + WEBSITE_API_URL + GETFORM_ROUTE_URL + `/${DISCREPANCY_CREATE_ROUTE}`;
    return this.httpService.get<any>(url, { skipLoader });
  }

  updateTimeTrackings(
    candidateId: string | number | null,
    ids: string[],
    skipLoader: boolean = false
  ) {
    // TODO: capire col backend che rotta serve
    const url = `${env.baseUrl}${BACKOFFICE_API_URL}${ROUTES.EMPLOYER}/${TIME_TRACKING_ROUTE}/${candidateId}/approve`;
    return this.httpService.post<any>(url, ids, { skipLoader });
  }


  getShiftsManagementActive(
    employerId: string | number | null, // id dell'azienda
    skipLoader: boolean = false
  ) {
    const url = `${env.baseUrl}${WEBSITE_API_URL}${EMPLOYER_ROUTE}/${employerId}/${SHIFTS_MANAGEMENT_ACTIVE_ROUTE}`;
    return this.httpService.get<any>(url, { skipLoader });
  }

  storeShiftManagementActive(
    employerId: string | number | null, // id dell'azienda
    active: boolean,
    skipLoader: boolean = false
  ) {
    const url = `${env.baseUrl}${WEBSITE_API_URL}/${EMPLOYER_ROUTE}/${employerId}/${SHIFTS_MANAGEMENT_ACTIVE_ROUTE}`;
    return this.httpService.post<any>(url, {active}, { skipLoader });
  }

  getShiftsManagementSettings(
    employerId: string | number | null,
    filters: any | null = null,
    skipLoader: boolean = false
  ) {
    const url = `${env.baseUrl}${WEBSITE_API_URL}${EMPLOYER_ROUTE}/${employerId}/${SHIFTS_MANAGEMENT_SETTINGS_ROUTE}`;
    const params = this.buildParams(filters);
    return this.httpService.get<any>(url, { skipLoader, params });
  }

  storeShiftsManagementSettings (
    employerId: string | number,
    data: any,
    skipLoader: boolean = false
  ) {
    // TODO: capire col backend che rotta serve
    const url = `${env.baseUrl}${WEBSITE_API_URL}${EMPLOYER_ROUTE}/${employerId}/${SHIFTS_MANAGEMENT_SETTINGS_ROUTE}`;
    return this.httpService.post<any>(url, data, { skipLoader });
  }


  updateShiftsManagementSettings (
    employerId: string | number,
    data: any,
    skipLoader: boolean = false
  ) {
    // TODO: capire col backend che rotta serve
    const url = `${env.baseUrl}${WEBSITE_API_URL}${EMPLOYER_ROUTE}/${employerId}/${SHIFTS_MANAGEMENT_SETTINGS_ROUTE}/${data.day_id}`;
    return this.httpService.put<any>(url, data, { skipLoader });
  }

  removeShiftsManagementSettings (
    employerId: string | number,
    data: any,
    skipLoader: boolean = false
  ) {
    // TODO: capire col backend che rotta serve
    const url = `${env.baseUrl}${WEBSITE_API_URL}${EMPLOYER_ROUTE}/${employerId}/${SHIFTS_MANAGEMENT_SETTINGS_ROUTE}/${data.id}`;
    return this.httpService.delete<any>(url, data );
  }

  getShiftsManagementTemplates(
    employerId: string | number | null,
    filters: any | null = null,
    skipLoader: boolean = false
  ) {
    const url = `${env.baseUrl}${WEBSITE_API_URL}${EMPLOYER_ROUTE}/${employerId}/${SHIFTS_MANAGEMENT_TEMPLATES_ROUTE}`;
    const params = this.buildParams(filters);
    return this.httpService.get<any>(url, { skipLoader, params });
  }

  storeShiftsManagementTemplates (
    employerId: string | number,
    data: any,
    skipLoader: boolean = false
  ) {
    // TODO: capire col backend che rotta serve
    const url = `${env.baseUrl}${WEBSITE_API_URL}${EMPLOYER_ROUTE}/${employerId}/${SHIFTS_MANAGEMENT_TEMPLATES_ROUTE}`;
    return this.httpService.post<any>(url, data, { skipLoader });
  }


  updateShiftsManagementTemplates (
    employerId: string | number,
    data: any,
    skipLoader: boolean = false
  ) {
    // TODO: capire col backend che rotta serve
    const url = `${env.baseUrl}${WEBSITE_API_URL}${EMPLOYER_ROUTE}/${employerId}/${SHIFTS_MANAGEMENT_TEMPLATES_ROUTE}`;
    return this.httpService.put<any>(url, data, { skipLoader });
  }

  removeShiftsManagementTemplates (
    employerId: string | number,
    data: any,
    skipLoader: boolean = false
  ) {
    // TODO: capire col backend che rotta serve
    const url = `${env.baseUrl}${WEBSITE_API_URL}${EMPLOYER_ROUTE}/${employerId}/${SHIFTS_MANAGEMENT_TEMPLATES_ROUTE}`;
    return this.httpService.delete<any>(url, data);
  }
}
